import {
  Badge,
  Box,
  Popover,
  Typography,
  useTheme
} from "@mui/material";
import { Simulado } from "../../services/api/simulados";
import { SegurancaBarragem } from "../../services/api/segBarragem";
import { Treinamento } from "../../services/api/treinamentos";
import { Seminario } from "../../services/api/seminarios";
import { useEffect, useRef, useState } from "react";
import { Add } from "@mui/icons-material";
import { CardInspecao } from "./cardInspecao";
import { CardSimulado } from "./cardSimulado";
import { CardTreinamento } from "./cardTreinamento";
import { CardSeminario } from "./cardSeminario";
import { useBarragemContext, useUserContext } from "../../context";
import { EventoBarragem } from "../../services/api/company";

export interface DiaMesProps {
  data: Date | string;
  isToday?: boolean;
  simulados: Simulado[];
  seminarios: Seminario[];
  treinamentos: Treinamento[];
  segBarragem: SegurancaBarragem[];
  onEdit?: (evento: EventoBarragem) => void;
  onRemove?: (evento: EventoBarragem) => void;
  onClick?: (
    data: Date,
    simulados: Simulado[],
    seminarios: Seminario[],
    treinamentos: Treinamento[],
    segBarragem: SegurancaBarragem[],
  ) => void;
}

export const DiaMes: React.FC<DiaMesProps> = ({ data, isToday, simulados, seminarios, treinamentos, segBarragem, onClick, onRemove, onEdit }) => {
  const { palette } = useTheme();
  const { barragem } = useBarragemContext();
  const user = useUserContext();

  const timerOpenPopup = useRef<NodeJS.Timeout>();
  const [open, setOpen] = useState(false);

  const sizeRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const buttonRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);


  useEffect(() => {
    setTimeout(() => {
      if (sizeRef.current) {
        const { width, height } = sizeRef.current.getBoundingClientRect();
        setDimensions({ width, height });
        const resizeObserver = new ResizeObserver(entries => {
          for (const entry of entries) {
            const { width, height } = entry.contentRect;
            setDimensions({ width, height });
          }
        });

        resizeObserver.observe(sizeRef.current);
        return () => resizeObserver.disconnect();
      }
    }, 50);
  }, [sizeRef.current]);

  useEffect(() => {
    if (timerOpenPopup.current) clearTimeout(timerOpenPopup.current);

    if (anchorEl) timerOpenPopup.current = setTimeout(() => setOpen(true), 500);

    return () => {
      if (timerOpenPopup.current) clearTimeout(timerOpenPopup.current);
    };
  }, [anchorEl]);

  // Cores do item
  const colors: string[] = [];
  if (simulados.length > 0) colors.push(palette.calendario.simuladoColor);
  if (seminarios.length > 0) colors.push(palette.calendario.seminarioColor);
  if (treinamentos.length > 0) colors.push(palette.calendario.treinamentoColor);
  if (segBarragem.length > 0) colors.push(palette.calendario.inspecaoColor);

  const gradientStops = colors.map((color, index) => {
    const startAngle = (index / colors.length) * 360;
    const endAngle = ((index + 1) / colors.length) * 360;
    return `${color} ${startAngle}deg ${endAngle}deg`;
  }).join(", ");

  const handleClick = () => {
    if (typeof data === "string") return;

    if (onClick) {
      onClick(data, simulados, seminarios, treinamentos, segBarragem);
      return;
    }
  };

  const handleMouseOver = () => {
    setAnchorEl(buttonRef.current);
  };

  const handleMouseOut = () => {
    if (timerOpenPopup.current) clearTimeout(timerOpenPopup.current);
    setAnchorEl(null);
    setOpen(false);
  };

  const componentPopUp: JSX.Element[] = [];

  let i = 0;
  const barragens = new Set<number>();
  for (const data of segBarragem) {
    if (barragem && !("error" in barragem) && barragem?.id !== data.idBarragem) {
      barragens.add(data.idBarragem);
    }

    componentPopUp.push(<CardInspecao
      key={i}
      inspecao={data}
      inspecaoAnterior={data.anterior}
      onEdit={onEdit}
      onRemove={(ev) => onRemove && onRemove({ tipo: "segurancaBarragem", evento: ev })}
    />);
    i++;
  }
  for (const data of simulados) {
    if (barragem && !("error" in barragem) && barragem?.id !== data.idBarragem) {
      barragens.add(data.idBarragem);
    }
    componentPopUp.push(<CardSimulado
      key={i}
      simulado={data}
      simuladoAnterior={data.anterior}
      onEdit={onEdit}
      onRemove={(ev) => onRemove && onRemove({ tipo: "simulado", evento: ev })}
    />);
    i++;
  }
  for (const data of treinamentos) {
    if (barragem && !("error" in barragem) && barragem?.id !== data.idBarragem) {
      barragens.add(data.idBarragem);
    }
    componentPopUp.push(<CardTreinamento
      key={i}
      treinamento={data}
      treinamentoAnterior={data.anterior}
      onEdit={onEdit}
      onRemove={(ev) => onRemove && onRemove({ tipo: "treinamento", evento: ev })}
    />);
    i++;
  }
  for (const data of seminarios) {
    if (barragem && !("error" in barragem) && barragem?.id !== data.idBarragem) {
      barragens.add(data.idBarragem);
    }
    componentPopUp.push(<CardSeminario
      key={i}
      seminario={data}
      seminarioAnterior={data.anterior}
      onEdit={onEdit}
      onRemove={(ev) => onRemove && onRemove({ tipo: "seminario", evento: ev })}
    />);
    i++;
  }

  if (!user || "error" in user) return <Typography fontWeight={700} fontSize={18}>!</Typography>;

  return <>
    <Popover
      open={componentPopUp.length > 0 && open}
      anchorEl={anchorEl}
      onClose={handleMouseOut}
      sx={{ pointerEvents: 'none' }}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      transformOrigin={{ vertical: "bottom", horizontal: "center" }}
      slotProps={{
        paper: {
          onMouseEnter: () => {
            setAnchorEl(buttonRef.current);
            setOpen(true);
          },
          onMouseLeave: handleMouseOut,
          sx: {
            boxShadow: 'none',
            pointerEvents: 'auto',
            background: 'transparent',
          },
        },
      }}
    >
      <div style={{ gap: "0.5em", display: "flex", flexDirection: "row", backgroundColor: "transparent" }}>
        {componentPopUp}
      </div>
    </Popover >

    <Box
      ref={sizeRef}
      sx={{
        flex: 1,
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRightColor: palette.secondary.main,
        borderRight: typeof data === "string" || data.getDay() === 6 ? '0.13em solid' : undefined,
      }}
    >
      {typeof data === "string" ?
        <Typography fontWeight={750} fontSize={"0.8rem"} color={palette.getContrastText(palette.primary.main)}>{data}</Typography> :
        <Box
          ref={buttonRef}
          onMouseEnter={handleMouseOver}
          onMouseLeave={handleMouseOut}
          onClick={handleClick}
          sx={{
            // flex: 1,
            display: "flex",
            cursor: "pointer",
            alignItems: "center",
            justifyContent: "center",
            border: isToday ? '0.13em solid' : undefined,
            borderColor: isToday ? palette.secondary.main : undefined,
            width: Math.min(dimensions.height, dimensions.width),
            height: Math.min(dimensions.height, dimensions.width),
            borderRadius: Math.min(dimensions.height, dimensions.width) / 2,

            background: `conic-gradient(${gradientStops})`,
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              bgcolor: 'action.hover',
              transform: 'scale(1.01)',
              background: `linear-gradient(45deg, ${palette.secondary.main})`,
            }
          }}
        >
          <Badge badgeContent={barragens.size} sx={{
            '& .MuiBadge-badge': {
              backgroundColor: palette.calendario.outraBarragemColor,
              minWidth: 12,
              height: 12,
              fontSize: '0.6rem',
              padding: 0,
              color: 'primary.main',
            },
          }}>
            <Typography fontWeight={750} fontSize={"0.8rem"} color={palette.getContrastText(palette.primary.main)}>
              {(open && user.admin) ? <Add fontSize="small" /> : data.getDate().toSignificantDigits('dia')}
            </Typography>
          </Badge>
        </Box>}
    </Box>
  </>;
};